import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowSquareOut, CheckCircle, Warning, Spinner } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback deep link options if not provided by API
const DEEP_LINK_OPTIONS = {
  "quick-canvass": ["/jobs", "/admin", "/reports"],
  "station-logbook": ["/entry", "/history", "/stations"],
  "project-command-center": ["/dashboard", "/work-orders", "/schedule"],
  "ask-stewie": ["/", "/admin", "/chat"],
};

export const AppsHubPage = () => {
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [error, setError] = useState(null);
  const [returnTo, setReturnTo] = useState({});
  const [openingApp, setOpeningApp] = useState(null);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/hub/sso/apps`);
        const data = await res.json();
        setApps(data.apps || []);
        
        // Initialize return paths with defaults
        const defaults = {};
        (data.apps || []).forEach(app => {
          defaults[app.key] = app.default_path || "/";
        });
        setReturnTo(defaults);
      } catch (e) {
        setError(e?.message || "Failed to load apps");
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  const openApp = async (appKey) => {
    try {
      setOpeningApp(appKey);
      const rt = returnTo[appKey] || "/";
      const res = await fetch(
        `${API_URL}/api/hub/apps/${appKey}/sso-link?return_to=${encodeURIComponent(rt)}`
      );
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to generate SSO link");
      }
      
      const url = data?.url;
      if (!url) throw new Error("No SSO URL returned");
      
      toast.success(`Opening ${appKey}...`);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.message || "Failed to generate SSO link");
      setOpeningApp(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="apps-hub-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">Loading apps...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="apps-hub-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="apps-hub-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            Apps Hub
          </h1>
          <p className="text-slate-600">
            Open any app via GETGRIDGATE SSO. Single sign-on across all your applications.
          </p>
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {apps.map((app) => {
            const enabled = !!app.enabled;
            const isOpening = openingApp === app.key;
            const deepLinks = app.deep_links?.length > 0 
              ? app.deep_links 
              : DEEP_LINK_OPTIONS[app.key] || [];
            
            return (
              <Card 
                key={app.key} 
                className={`border-slate-200 ${!enabled ? 'opacity-75' : ''}`}
                data-testid={`app-card-${app.key}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg font-display">{app.name}</CardTitle>
                      <CardDescription className="text-xs font-mono mt-1">
                        {app.key}
                      </CardDescription>
                    </div>
                    <Badge 
                      className={enabled 
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200" 
                        : "bg-amber-100 text-amber-800 border-amber-200"
                      }
                    >
                      {enabled ? (
                        <><CheckCircle size={12} className="mr-1" /> SSO Ready</>
                      ) : (
                        <><Warning size={12} className="mr-1" /> Needs Setup</>
                      )}
                    </Badge>
                  </div>
                  {!enabled && app.reason && (
                    <p className="text-xs text-amber-600 mt-2">
                      Reason: {app.reason.replace(/_/g, ' ')}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`return-${app.key}`} className="text-xs text-slate-500">
                      Deep link path
                    </Label>
                    
                    {deepLinks.length > 0 ? (
                      <Select
                        value={returnTo[app.key] || app.default_path || "/"}
                        onValueChange={(value) => setReturnTo((prev) => ({ 
                          ...prev, 
                          [app.key]: value 
                        }))}
                        disabled={!enabled}
                      >
                        <SelectTrigger 
                          id={`return-${app.key}`}
                          className="font-mono text-sm"
                          data-testid={`app-return-to-${app.key}`}
                        >
                          <SelectValue placeholder="Select path" />
                        </SelectTrigger>
                        <SelectContent>
                          {deepLinks.map((path) => (
                            <SelectItem key={path} value={path} className="font-mono">
                              {path}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`return-${app.key}`}
                        value={returnTo[app.key] || app.default_path || "/"}
                        onChange={(e) => setReturnTo((prev) => ({ 
                          ...prev, 
                          [app.key]: e.target.value 
                        }))}
                        placeholder={app.default_path || "/"}
                        disabled={!enabled}
                        className="font-mono text-sm"
                        data-testid={`app-return-to-${app.key}`}
                      />
                    )}
                  </div>

                  <Button
                    onClick={() => openApp(app.key)}
                    disabled={!enabled || isOpening}
                    className="w-full"
                    data-testid={`app-open-btn-${app.key}`}
                  >
                    {isOpening ? (
                      <>
                        <Spinner size={16} className="mr-2 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <ArrowSquareOut size={16} className="mr-2" />
                        Open {app.name}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {apps.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No apps configured yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default AppsHubPage;
